/* eslint-disable react-perf/jsx-no-new-function-as-prop */
import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from '@mui/material';
import { ButtonGroupWrapper, MoreButtonCDS } from '@sharedCR/coreDesignSystem/Buttons';
import { DocumentIcon, EditPenIcon, ScheduleNotSetIcon } from '@sharedCR/coreDesignSystem/icons';
import MenuPopoverCDS from '@sharedCR/coreDesignSystem/MenuPopoverCDS';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { IconSize } from 'constants/coreDesignSystem';
import { PAGE_CODES } from 'constants/pageCodes';
import { HORIZONTAL_ALIGNMENT } from 'constants/ui';
import usePopover from 'hooks/usePopover';
import { getConditionalArray } from 'utils/data';
import { checkIsInvalidImpounds } from 'utils/impounds';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import {
  closeConfirmationDialogAction,
  openConfirmationDialogAction,
  openDialogAction,
} from 'store/global/actions';
import {
  selectExtendedImpounds,
  selectIsLoanCanceled,
  selectLoanFeesSummary,
  selectTaxesInsuranceEscrowedLoanFeesView,
} from 'store/loanApplications/selectors';

import { LoanFeesTestIds } from '../../../../LoanFees.testIds';
import ImpoundScheduleTable from '../ImpoundScheduleTable';

import { getCollectedImpoundsSchedule } from './FeesMoreIcon.utils';

const FeesMoreIcon = ({
  onOpenSection,
  cdSection,
  onToggleAccordion,
  editedSection,
  isLoading,
}) => {
  const dispatch = useDispatch();

  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const { isManageAllowed: isPageManageAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_FEES),
  );
  const isLoanCanceled = useSelector(selectIsLoanCanceled);
  const impoundsData = useSelector(selectExtendedImpounds);
  const { firstPaymentDate: prepaidInterestCreditFirstPaymentDate } =
    useSelector(selectLoanFeesSummary);
  const taxesInsuranceEscrowed = useSelector(selectTaxesInsuranceEscrowedLoanFeesView);

  const actionAnchorId = anchorEl ? 'action-popover' : undefined;

  const isInvalidImpoundExists = useMemo(
    () => checkIsInvalidImpounds(impoundsData.impounds, { prepaidInterestCreditFirstPaymentDate }),
    [impoundsData.impounds, prepaidInterestCreditFirstPaymentDate],
  );

  const isSectionG = cdSection === 'G';
  const isCheckImpoundScheduleVisible = isSectionG;
  const isCheckImpoundScheduleDisabled =
    isLoading || !prepaidInterestCreditFirstPaymentDate || isInvalidImpoundExists;

  const isManageAllowed = isPageManageAllowed && !isLoanCanceled;

  const { aggregateAdjustment, schedule } = useMemo(
    () =>
      getCollectedImpoundsSchedule({
        impoundsData,
        taxesInsuranceEscrowed,
        prepaidInterestCreditFirstPaymentDate,
      }),
    [impoundsData, taxesInsuranceEscrowed, prepaidInterestCreditFirstPaymentDate],
  );

  const onActionClick = useCallback(
    e => {
      onSetAnchorElement(e);
      onToggleAccordion(true);
    },
    [onToggleAccordion, onSetAnchorElement],
  );

  const onEdit = useCallback(
    e => {
      onClearAnchorElement(e);

      return new Promise((res, rej) => {
        if (editedSection?.isFormChanged) {
          dispatch(
            openConfirmationDialogAction({
              type: ConfirmationModalType.WARNING,
              message: 'Unsaved Changes',
              description: 'Apply recent changes to the Fees?',
              customButtons: (
                <ButtonGroupWrapper
                  alignment={HORIZONTAL_ALIGNMENT.CENTER}
                  className="w-full flex-wrap"
                >
                  <Button
                    data-testid={LoanFeesTestIds.FEES_UNSAVED_DISCARD_BUTTON}
                    size="large"
                    variant="line"
                    onClick={() => {
                      editedSection.ref.current.onCancelForm();
                      dispatch(closeConfirmationDialogAction());
                    }}
                  >
                    Discard and Close
                  </Button>

                  <Button
                    data-testid={LoanFeesTestIds.FEES_UNSAVED_CONTINUE_BUTTON}
                    size="large"
                    onClick={() => {
                      setTimeout(() => {
                        editedSection.ref.current.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start',
                          inline: 'start',
                        });
                      }, 0);

                      dispatch(closeConfirmationDialogAction());
                    }}
                  >
                    Continue Edit
                  </Button>

                  <Button
                    data-testid={LoanFeesTestIds.FEES_UNSAVED_APPLY_BUTTON}
                    size="large"
                    onClick={() => {
                      editedSection.ref.current
                        .onSubmit()
                        .then(() => dispatch(closeConfirmationDialogAction()));
                    }}
                  >
                    Apply
                  </Button>
                </ButtonGroupWrapper>
              ),
            }),
          );

          return rej();
        }

        onOpenSection(e);

        return res();
      });
    },
    [dispatch, editedSection, onOpenSection, onClearAnchorElement],
  );

  const onOpenImpoundSchedule = useCallback(
    e => {
      onClearAnchorElement(e);

      dispatch(
        openDialogAction({
          fitContentWidth: true,
          header: 'Impound Schedule',
          closeButtonDataTestId: LoanFeesTestIds.IMPOUND_SCHEDULE_CLOSE_BUTTON,
          body: () => (
            <ImpoundScheduleTable aggregateAdjustment={aggregateAdjustment} schedule={schedule} />
          ),
        }),
      );
    },
    [aggregateAdjustment, dispatch, onClearAnchorElement, schedule],
  );

  const items = useMemo(
    () => [
      ...getConditionalArray(isManageAllowed, [
        {
          text: 'Edit',
          onClick: e => onEdit(e).catch(Error),
          icon: <EditPenIcon size={IconSize[20]} />,
          'data-id': 'on-open-section',
          'data-section': cdSection,
          isDisabled: isLoading,
        },
        {
          text: 'Add Fee',
          onClick: e => onEdit(e).catch(Error),
          icon: <DocumentIcon size={IconSize[20]} />,
          'data-id': 'on-add-fee',
          'data-is-to-add': 'true',
          'data-section': cdSection,
          isDisabled: isLoading || !taxesInsuranceEscrowed,
        },
      ]),
      ...getConditionalArray(isCheckImpoundScheduleVisible, [
        {
          text: 'Check Impound Schedule',
          onClick: onOpenImpoundSchedule,
          icon: <ScheduleNotSetIcon size={IconSize[20]} />,
          isDisabled: isCheckImpoundScheduleDisabled,
          'data-id': 'on-check-impound-schedule',
        },
      ]),
    ],
    [
      isManageAllowed,
      isCheckImpoundScheduleVisible,
      onEdit,
      onOpenImpoundSchedule,
      cdSection,
      isCheckImpoundScheduleDisabled,
      isLoading,
      taxesInsuranceEscrowed,
    ],
  );

  return (
    <>
      <MoreButtonCDS
        aria-describedby={actionAnchorId}
        isSelected={!!anchorEl}
        onClick={onActionClick}
      />

      <MenuPopoverCDS
        anchorEl={anchorEl}
        id={actionAnchorId}
        items={items}
        onClose={onClearAnchorElement}
      />
    </>
  );
};

FeesMoreIcon.propTypes = {
  ...WithLoadingPropTypes,
  cdSection: PropTypes.string.isRequired,
  onOpenSection: PropTypes.func.isRequired,
  onToggleAccordion: PropTypes.func.isRequired,
  editedSection: PropTypes.object,
};

export default compose(memo)(FeesMoreIcon);
